import * as XLSX from 'xlsx';
import type { PageResult } from '../types';

export type ExportSection = 'accessibility' | 'eaa' | 'seo' | 'geo' | 'brokenLinks' | 'toxicLinks';

export const ALL_SECTIONS: ExportSection[] = [
  'accessibility',
  'eaa',
  'seo',
  'geo',
  'brokenLinks',
  'toxicLinks',
];

export const SECTION_LABELS: Record<ExportSection, string> = {
  accessibility: 'Accessibility',
  eaa: 'EAA',
  seo: 'SEO',
  geo: 'GEO',
  brokenLinks: 'Broken Links',
  toxicLinks: 'Toxic Links',
};

const HEADERS = [
  'Section',
  'Severity',
  'Page URL',
  'Issue Type',
  'Title',
  'Description',
  'Impact',
  'DOM Element',
  'How to Fix',
  'Reference URL',
  'WCAG Criteria',
  'WCAG Level',
  'Status Code',
  'Toxicity Score',
  'Toxicity Flags',
];

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  serious: 1,
  moderate: 2,
  minor: 3,
  warning: 4,
  info: 5,
};

function truncate(str: string, max = 500): string {
  return str.length > max ? str.slice(0, max) + '…' : str;
}

type Row = (string | number)[];

function buildAccessibilityRows(pages: PageResult[]): Row[] {
  const rows: Row[] = [];
  for (const page of pages) {
    for (const issue of page.accessibilityIssues ?? []) {
      for (const node of issue.nodes) {
        rows.push([
          'Accessibility',
          issue.impact,
          page.pageUrl,
          issue.ruleId,
          issue.ruleId.replace(/-/g, ' '),
          issue.description,
          `${issue.impact.charAt(0).toUpperCase() + issue.impact.slice(1)} accessibility issue`,
          truncate(node.target.join(', ') + (node.html ? ' — ' + node.html : '')),
          node.failureSummary ?? '',
          issue.helpUrl ?? '',
          issue.wcagCriteria.join(' | '),
          issue.wcagLevel,
          '',
          '',
          '',
        ]);
      }
    }
  }
  return rows;
}

function buildEaaRows(pages: PageResult[]): Row[] {
  const rows: Row[] = [];
  for (const page of pages) {
    for (const issue of page.eaaIssues ?? []) {
      const howToFix = issue.remediationGuidance?.howToFix.join(' | ') ?? issue.remediation ?? '';
      const impact = issue.remediationGuidance?.whyItMatters ?? '';
      const ref = issue.helpUrl ?? '';
      const wcag = issue.wcagMapping?.join(' | ') ?? '';
      const issueType = issue.ruleId ?? issue.clauseNumber;
      if (issue.affectedElements && issue.affectedElements.length > 0) {
        for (const el of issue.affectedElements) {
          rows.push([
            'EAA', issue.severity, el.pageUrl || page.pageUrl, issueType,
            issue.requirement, issue.description, impact,
            truncate(el.selector + (el.html ? ' — ' + el.html : '')),
            howToFix, ref, wcag, '', '', '', '',
          ]);
        }
      } else {
        rows.push([
          'EAA', issue.severity, issue.pageUrl ?? page.pageUrl, issueType,
          issue.requirement, issue.description, impact,
          truncate(issue.element ?? ''),
          howToFix, ref, wcag, '', '', '', '',
        ]);
      }
    }
  }
  return rows;
}

function buildSeoRows(pages: PageResult[]): Row[] {
  const rows: Row[] = [];
  for (const page of pages) {
    for (const issue of page.seoResult?.issues ?? []) {
      rows.push([
        'SEO', issue.severity, page.pageUrl, issue.type, issue.title,
        issue.description, issue.impact, truncate(issue.element ?? ''),
        issue.recommendation, issue.googleDocsUrl ?? '',
        '', '', '', '', '',
      ]);
    }
  }
  return rows;
}

function buildGeoRows(pages: PageResult[]): Row[] {
  const rows: Row[] = [];
  for (const page of pages) {
    for (const issue of page.geoResult?.issues ?? []) {
      rows.push([
        'GEO', issue.severity, issue.pageUrl ?? page.pageUrl, issue.type,
        issue.title, issue.description, issue.impact,
        truncate(issue.element ?? ''), issue.recommendation,
        issue.referenceUrl ?? '', '', '', '', '', '',
      ]);
    }
  }
  return rows;
}

function buildBrokenLinkRows(pages: PageResult[]): Row[] {
  const rows: Row[] = [];
  for (const page of pages) {
    for (const link of page.brokenLinks ?? []) {
      // A link the probe never settled on is not a confirmed break — exported as
      // manual review so it is never read as "this link is dead".
      if (link.needsManualReview) {
        rows.push([
          'Broken Links', 'manual review', link.sourceUrl,
          'unverified-link',
          'Link could not be verified',
          `${link.isExternal ? 'External' : 'Internal'} link to ${link.targetUrl} refused or never answered the scanner (HTTP ${link.statusCode || 'no response'})`,
          'Usually a live page behind a CAPTCHA or bot filter — not counted as broken',
          link.linkText ?? '',
          `Open ${link.targetUrl} in a browser and confirm it works`,
          link.targetUrl, '', '', link.statusCode, '', '',
        ]);
        continue;
      }
      const severity = link.statusCode >= 500 ? 'critical' : 'serious';
      rows.push([
        'Broken Links', severity, link.sourceUrl,
        `broken-link-${link.statusCode}`,
        `Broken Link (${link.statusCode})`,
        `${link.isExternal ? 'External' : 'Internal'} link to ${link.targetUrl} returns HTTP ${link.statusCode}`,
        'Broken links harm user experience and SEO crawlability',
        link.linkText ?? '',
        `Update or remove the link to ${link.targetUrl}`,
        link.targetUrl, '', '', link.statusCode, '', '',
      ]);
    }
  }
  return rows;
}

function buildToxicLinkRows(pages: PageResult[]): Row[] {
  const rows: Row[] = [];
  for (const page of pages) {
    for (const link of page.toxicLinks ?? []) {
      // Same rule as broken links: unverifiable link → manual review, unscored.
      if (link.needsManualReview) {
        rows.push([
          'Toxic Links', 'manual review', link.sourceUrl, 'unverified-link',
          'Link could not be verified',
          `Outbound link to ${link.targetUrl} (${link.targetDomain}) refused or never answered the scanner (HTTP ${link.statusCode || 'no response'})`,
          'Usually a live page behind a CAPTCHA or bot filter — not scored',
          link.linkText ?? '',
          `Open ${link.targetUrl} in a browser and confirm it works`,
          link.targetUrl, '', '',
          link.statusCode !== undefined ? link.statusCode : '',
          '', '',
        ]);
        continue;
      }
      const severity = link.toxicityScore >= 70 ? 'critical' : link.toxicityScore >= 40 ? 'serious' : 'moderate';
      rows.push([
        'Toxic Links', severity, link.sourceUrl, 'toxic-outbound-link',
        'Toxic Outbound Link',
        `Outbound link to ${link.targetUrl} (${link.targetDomain}) has a toxicity score of ${link.toxicityScore}/100`,
        'Links to toxic or spammy sites can harm SEO rankings and brand reputation',
        link.linkText ?? '',
        `Review and consider removing or nofollowing the link to ${link.targetDomain}`,
        link.targetUrl, '', '',
        link.statusCode !== undefined ? link.statusCode : '',
        link.toxicityScore,
        link.flags.join(' | '),
      ]);
    }
  }
  return rows;
}

const BUILDERS: Record<ExportSection, (pages: PageResult[]) => Row[]> = {
  accessibility: buildAccessibilityRows,
  eaa: buildEaaRows,
  seo: buildSeoRows,
  geo: buildGeoRows,
  brokenLinks: buildBrokenLinkRows,
  toxicLinks: buildToxicLinkRows,
};

function sortBySeverity(rows: Row[]): Row[] {
  return [...rows].sort((a, b) => (SEVERITY_ORDER[a[1] as string] ?? 99) - (SEVERITY_ORDER[b[1] as string] ?? 99));
}

function getFilename(siteUrl: string, ext: 'csv' | 'xlsx'): string {
  const domain = siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '').replace(/[^a-z0-9.-]/gi, '-');
  const date = new Date().toISOString().split('T')[0];
  return `issues-${domain}-${date}.${ext}`;
}

/** Returns how many rows each section would produce — used to show counts in the UI */
export function getSectionCounts(pageResults: PageResult[]): Record<ExportSection, number> {
  const counts = {} as Record<ExportSection, number>;
  for (const section of ALL_SECTIONS) {
    counts[section] = BUILDERS[section](pageResults).length;
  }
  return counts;
}

// ─── CSV ────────────────────────────────────────────────────────────────────

function csvCell(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  const str = String(value);
  if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

export function exportIssuesCsv(
  pageResults: PageResult[],
  siteUrl: string,
  sections: ExportSection[] = ALL_SECTIONS,
): void {
  const allRows: Row[] = [];
  for (const section of ALL_SECTIONS) {
    if (sections.includes(section)) {
      allRows.push(...sortBySeverity(BUILDERS[section](pageResults)));
    }
  }

  const csvLines = [
    HEADERS.map(csvCell).join(','),
    ...allRows.map((row) => row.map(csvCell).join(',')),
  ];

  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = getFilename(siteUrl, 'csv');
  a.click();
  URL.revokeObjectURL(url);
}

// ─── CLAUDE ANALYSIS ─────────────────────────────────────────────────────────

const CLAUDE_HEADERS = [
  'Section',
  'Severity',
  'Issue Type',
  'Title',
  'Pages Affected',
  'Total Instances',
  'Description',
  'Impact',
  'How to Fix',
  'Reference URL',
  'WCAG Criteria',
  'WCAG Level',
  'Example Page URL',
  'Example Element',
];

interface DedupedIssue {
  section: string;
  severity: string;
  issueType: string;
  title: string;
  pagesAffected: Set<string>;
  instances: number;
  description: string;
  impact: string;
  howToFix: string;
  referenceUrl: string;
  wcagCriteria: string;
  wcagLevel: string;
  examplePageUrl: string;
  exampleElement: string;
}

function worstSeverity(a: string, b: string): string {
  return (SEVERITY_ORDER[a] ?? 99) <= (SEVERITY_ORDER[b] ?? 99) ? a : b;
}

function buildDedupedRows(pageResults: PageResult[], sections: ExportSection[]): Row[] {
  const map = new Map<string, DedupedIssue>();

  const upsert = (
    key: string,
    section: string,
    severity: string,
    issueType: string,
    title: string,
    pageUrl: string,
    description: string,
    impact: string,
    howToFix: string,
    referenceUrl: string,
    wcagCriteria: string,
    wcagLevel: string,
    element: string,
  ) => {
    const existing = map.get(key);
    if (existing) {
      existing.severity = worstSeverity(existing.severity, severity);
      existing.pagesAffected.add(pageUrl);
      existing.instances++;
    } else {
      map.set(key, {
        section, severity, issueType, title,
        pagesAffected: new Set([pageUrl]),
        instances: 1,
        description, impact, howToFix, referenceUrl,
        wcagCriteria, wcagLevel,
        examplePageUrl: pageUrl,
        exampleElement: element.slice(0, 150),
      });
    }
  };

  for (const page of pageResults) {
    if (sections.includes('accessibility')) {
      for (const issue of page.accessibilityIssues ?? []) {
        const key = `accessibility:${issue.ruleId}`;
        const firstNode = issue.nodes[0];
        for (const node of issue.nodes) {
          upsert(
            key, 'Accessibility', issue.impact, issue.ruleId,
            issue.ruleId.replace(/-/g, ' '),
            page.pageUrl,
            issue.description,
            `${issue.impact} accessibility violation`,
            node.failureSummary ?? '',
            issue.helpUrl ?? '',
            issue.wcagCriteria.join(' | '),
            issue.wcagLevel,
            firstNode?.target.join(', ') ?? '',
          );
        }
      }
    }

    if (sections.includes('eaa')) {
      for (const issue of page.eaaIssues ?? []) {
        const key = `eaa:${issue.ruleId ?? issue.clauseNumber}`;
        const howToFix = issue.remediationGuidance?.howToFix.join(' | ') ?? issue.remediation ?? '';
        const impact = issue.remediationGuidance?.whyItMatters ?? '';
        const firstEl = issue.affectedElements?.[0];
        const exampleElement = firstEl?.selector ?? issue.element ?? '';
        const pageUrl = firstEl?.pageUrl ?? issue.pageUrl ?? page.pageUrl;
        const elements = issue.affectedElements?.length ? issue.affectedElements : [null];
        for (const el of elements) {
          upsert(
            key, 'EAA', issue.severity,
            issue.ruleId ?? issue.clauseNumber,
            issue.requirement,
            el ? el.pageUrl : pageUrl,
            issue.description, impact, howToFix,
            issue.helpUrl ?? '',
            issue.wcagMapping?.join(' | ') ?? '',
            '',
            el ? el.selector : exampleElement,
          );
        }
      }
    }

    if (sections.includes('seo')) {
      for (const issue of page.seoResult?.issues ?? []) {
        upsert(
          `seo:${issue.type}`, 'SEO', issue.severity, issue.type,
          issue.title, page.pageUrl,
          issue.description, issue.impact, issue.recommendation,
          issue.googleDocsUrl ?? '', '', '',
          issue.element ?? '',
        );
      }
    }

    if (sections.includes('geo')) {
      for (const issue of page.geoResult?.issues ?? []) {
        upsert(
          `geo:${issue.type}`, 'GEO', issue.severity, issue.type,
          issue.title, issue.pageUrl ?? page.pageUrl,
          issue.description, issue.impact, issue.recommendation,
          issue.referenceUrl ?? '', '', '',
          issue.element ?? '',
        );
      }
    }

    if (sections.includes('brokenLinks')) {
      for (const link of page.brokenLinks ?? []) {
        if (link.needsManualReview) {
          upsert(
            `brokenLinks:unverified:${link.targetUrl}`, 'Broken Links', 'manual review',
            'unverified-link',
            'Link could not be verified',
            link.sourceUrl,
            `${link.isExternal ? 'External' : 'Internal'} link refused or never answered the scanner (HTTP ${link.statusCode || 'no response'})`,
            'Usually a live page behind a CAPTCHA or bot filter — not counted as broken',
            `Open ${link.targetUrl} in a browser and confirm it works`,
            link.targetUrl, '', '',
            link.targetUrl,
          );
          continue;
        }
        const severity = link.statusCode >= 500 ? 'critical' : 'serious';
        upsert(
          `brokenLinks:${link.targetUrl}`, 'Broken Links', severity,
          `broken-link-${link.statusCode}`,
          `Broken Link (${link.statusCode})`,
          link.sourceUrl,
          `${link.isExternal ? 'External' : 'Internal'} link returns HTTP ${link.statusCode}`,
          'Broken links harm user experience and SEO crawlability',
          `Update or remove the link to ${link.targetUrl}`,
          link.targetUrl, '', '',
          link.targetUrl,
        );
      }
    }

    if (sections.includes('toxicLinks')) {
      for (const link of page.toxicLinks ?? []) {
        if (link.needsManualReview) {
          upsert(
            `toxicLinks:unverified:${link.targetUrl}`, 'Toxic Links', 'manual review',
            'unverified-link',
            'Link could not be verified',
            link.sourceUrl,
            `Outbound link to ${link.targetDomain} refused or never answered the scanner (HTTP ${link.statusCode || 'no response'})`,
            'Usually a live page behind a CAPTCHA or bot filter — not scored',
            `Open ${link.targetUrl} in a browser and confirm it works`,
            link.targetUrl, '', '',
            link.targetUrl,
          );
          continue;
        }
        const severity = link.toxicityScore >= 70 ? 'critical' : link.toxicityScore >= 40 ? 'serious' : 'moderate';
        upsert(
          `toxicLinks:${link.targetDomain}`, 'Toxic Links', severity,
          'toxic-outbound-link',
          `Toxic Domain: ${link.targetDomain}`,
          link.sourceUrl,
          `Links to ${link.targetDomain} — toxicity score ${link.toxicityScore}/100. Flags: ${link.flags.join(', ')}`,
          'Links to toxic or spammy sites can harm SEO rankings',
          `Review and remove or nofollow all links to ${link.targetDomain}`,
          link.targetUrl, '', '',
          link.targetUrl,
        );
      }
    }
  }

  const rows: Row[] = [...map.values()]
    .sort((a, b) => {
      const sectionOrder: Record<string, number> = {
        Accessibility: 0, EAA: 1, SEO: 2, GEO: 3, 'Broken Links': 4, 'Toxic Links': 5,
      };
      const sd = (sectionOrder[a.section] ?? 99) - (sectionOrder[b.section] ?? 99);
      if (sd !== 0) return sd;
      return (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99);
    })
    .map((d) => [
      d.section,
      d.severity,
      d.issueType,
      d.title,
      d.pagesAffected.size,
      d.instances,
      d.description,
      d.impact,
      d.howToFix,
      d.referenceUrl,
      d.wcagCriteria,
      d.wcagLevel,
      d.examplePageUrl,
      d.exampleElement,
    ]);

  return rows;
}

/** Row count for the Claude Analysis format */
export function getClaudeRowCount(pageResults: PageResult[]): number {
  return buildDedupedRows(pageResults, ALL_SECTIONS).length;
}

export function exportClaudeAnalysisCsv(
  pageResults: PageResult[],
  siteUrl: string,
  sections: ExportSection[] = ALL_SECTIONS,
): void {
  const rows = buildDedupedRows(pageResults, sections);
  const csvLines = [
    CLAUDE_HEADERS.map(csvCell).join(','),
    ...rows.map((row) => row.map(csvCell).join(',')),
  ];
  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const domain = siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '').replace(/[^a-z0-9.-]/gi, '-');
  const date = new Date().toISOString().split('T')[0];
  a.href = url;
  a.download = `issues-claude-${domain}-${date}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── XLSX ────────────────────────────────────────────────────────────────────

export function exportIssuesXlsx(
  pageResults: PageResult[],
  siteUrl: string,
  sections: ExportSection[] = ALL_SECTIONS,
): void {
  const wb = XLSX.utils.book_new();

  for (const section of ALL_SECTIONS) {
    if (!sections.includes(section)) continue;
    const rows = sortBySeverity(BUILDERS[section](pageResults));
    if (rows.length === 0) continue;

    // Strip the 'Section' column — redundant when each tab is already named
    const sheetHeaders = HEADERS.slice(1);
    const sheetRows = rows.map((r) => r.slice(1));

    const ws = XLSX.utils.aoa_to_sheet([sheetHeaders, ...sheetRows]);

    // Column widths
    ws['!cols'] = sheetHeaders.map((h, i) => {
      const maxLen = Math.max(
        h.length,
        ...sheetRows.map((r) => String(r[i] ?? '').length).slice(0, 100),
      );
      return { wch: Math.min(maxLen + 2, 80) };
    });

    XLSX.utils.book_append_sheet(wb, ws, SECTION_LABELS[section]);
  }

  XLSX.writeFile(wb, getFilename(siteUrl, 'xlsx'));
}
